import React, {
  Fragment,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import classNames from 'classnames';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
} from '@dnd-kit/core';
import pluralize from 'pluralize';
import pMap from 'p-map-browser';

import getConfig from '../../../../../../../util/config';
import { PhotoSelectorContext } from '../../../../../../../components/global/PhotoSelectorContext';
import { ALBUM_MANUAL_MATCHING_FILTERS } from './constants';
import AlbumManualMatchingHeader from './components/AlbumManualMatchingHeader';
import AlbumManualMatchingToolbar from './components/AlbumManualMatchingToolbar';
import Spinner from '../../../../../../../components/global/Spinner';
import PhotoGrid from '../../../../../../../components/grid/PhotoGrid';
import ShiftSelect from '../../../../../../../components/global/ShiftSelect';
import Button from '../../../../../../../components/global/Button';
import Angle from '../../../../../../../components/global/Angle';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import IdentitySelector from './components/IdentitySelector';
import IdentitySelectorOption from './components/IdentitySelectorOptionDraggable/IdentitySelectorOption';
import PhotoGridCardDroppable from './components/PhotoGridCardDroppable';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import useGlobalToast from '../../../../../../../hooks/useGlobalToast';
import { DROPPABLE_ELEMENT_TYPES } from '../../../../../../../constants/dnd';
import useAlbumManualMatching from './hooks/useAlbumManualMatching';
import useAccount from '../../../../../../../components/global/hooks/useAccount';
import { VOTE_DECISION } from '../../../../../../../constants/vote-decisions';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';
import IdentityAvatar from '../../../../../../../components/global/identity/IdentityAvatar';
import { getIdentityFullName } from '../../../../../../../util/identity';
import Alert from '../../../../../../../components/global/Alert';
import { getLargestFace } from './helpers';
import {
  ALBUM_PHOTO_FILTERS,
  ALBUM_PHOTO_STATUS,
} from '../../../../../../../constants/albums';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../../hooks/useCloseModal';
import { CONFIRM_MODAL } from '../../../../../../../constants/modals';
import PhotoDownloadModal from '../../../../../../../components/global/PhotoDownload/modal';
import PhotoAlbumFolderTagsModal from '../../../components/PhotoAlbumFolderTags/PhotoAlbumFolderTagsModal';
import ProMultiTargetSelectionModal from '../../../../../components/MultiTargetSelection/components/ProMultiTargetSelection/modal';
import FaceTagPhotoModal from '../../../../FaceTagPhoto/modal';
import AlbumPhotoMenu from '../../../../../../../components/global/AlbumPhotoMenu';
import useBasename from '../../../../../../../components/routing/useBasename';
import PhotoGroupPhotoViewerActions from '../PhotoGroupPhotoViewerActions';

const propTypes = {
  headerless: PropTypes.bool,
};

const AlbumManualMatching = ({ headerless = false }) => {
  const scrollRef = useRef(null);
  const openAlert = useOpenAlert();
  const requestConfirm = useRequestConfirm();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const basename = useBasename();
  const match = useRouteMatch();
  const { albumId } = match?.params ?? {};
  const [draggingIdentity, setDraggingIdentity] = useState(null);
  const [faceTagModePhotoId, setFaceTagModePhotoId] = useState(null);
  const [faceTagTotallyVoteCast, setFaceTagTotallyVoteCast] = useState(false);
  const [useFauxTaggingMode, setUseFauxTaggingMode] = useState(false);
  const [availableIdentities, setAvailableIdentities] = useState([]);
  const { setToast } = useGlobalToast();
  const {
    selectedPhotos,
    selectionEnabled,
    onSelectPhotos,
    onDeselectPhotos,
    onResetSelection,
    onToggleSelectionEnabled,
    selectionSettings: { showFileNames },
  } = useContext(PhotoSelectorContext);

  const { account } = useAccount();

  useEffect(() => {
    if (selectionEnabled && selectedPhotos.length === 0) {
      onToggleSelectionEnabled();
    }
  }, [selectedPhotos]);

  const {
    album,
    loading,
    loadingMore,
    photos,
    photosTotal,
    loadMore,
    refetch,
    filter,
    onFilterUpdate,
    onBulkVote,
    onIdentityPubBoxCreate,
    onPhotosDownload,
    onPhotosDelete,
    onPhotosHide,
    onPhotosPublish,
    onPhotosMove,
  } = useAlbumManualMatching({ albumId });

  const getAdjacentFaceTagModeRecordId = adjacency => {
    const currentRecordIdIndex = selectedPhotos.findIndex(
      id => id === faceTagModePhotoId,
    );
    const nextRecordId = selectedPhotos[currentRecordIdIndex + 1];
    const prevRecordId = selectedPhotos[currentRecordIdIndex - 1];

    if (currentRecordIdIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecordId) {
      return nextRecordId;
    }

    if (adjacency === -1 && prevRecordId) {
      return prevRecordId;
    }

    return undefined;
  };

  const nextRecordId = useMemo(
    () => getAdjacentFaceTagModeRecordId(1),
    [selectedPhotos, faceTagModePhotoId, setFaceTagModePhotoId],
  );
  const prevRecordId = useMemo(
    () => getAdjacentFaceTagModeRecordId(-1),
    [selectedPhotos, faceTagModePhotoId, setFaceTagModePhotoId],
  );

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 50,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const onDragStart = ({ active }) => {
    setDraggingIdentity(active.data.current.identity);
  };

  const onDragEnd = async ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (
      over?.data?.current?.type !==
      DROPPABLE_ELEMENT_TYPES.MANUAL_MATCHING_PHOTO_GRID_CARD
    ) {
      return;
    }

    const { identity } = active?.data?.current ?? {};
    const { current_face: currentFace, current_matching_face: matchingFace } =
      identity ?? {};

    if (
      !selectionEnabled ||
      (selectionEnabled && selectedPhotos.length === 1)
    ) {
      const photo = selectionEnabled
        ? photos.find(p => p.id === selectedPhotos[0])
        : over?.data?.current?.photo;
      const faces = photo?.faces?.records ?? [];
      const largestFace = getLargestFace(faces);

      // Check if faux tagging mode is enabled
      if (useFauxTaggingMode) {
        // Always use faux tags when toggle is ON
        const result = await onIdentityPubBoxCreate({
          identityId: identity?.id,
          photoId: photo?.id,
        });

        if (result.success) {
          setToast(`${identity.name} has been tagged successfully!`);
          await refetch();
        } else {
          openAlert({
            type: 'danger',
            title: 'Error',
            message:
              'There was an error tagging this photo. Please try again later.',
          });
        }
      } else {
        // Use current behavior: match to detected faces only
        if (largestFace) {
          await onBulkVote({
            votes: [
              {
                identity_id: identity?.id,
                compared_face_id: largestFace?.id,
                compared_photo_id: photo?.id,
                selfie_face_id: currentFace?.id ?? matchingFace?.id,
                decision: VOTE_DECISION.TOTALLY,
                judge_id: account?.id,
                is_consumer: false,
                applied: true,
              },
            ],
          });
          setToast(`${identity.name} has been matched successfully!`);
        } else if (faces.length > 1) {
          openAlert({
            type: 'note',
            title: '',
            message: `There are multiple similarly sized faces in this photo. To tag this identity to the proper face, click into the photo and then click on the Face Tag icon in the top right`,
          });
        } else {
          // No faces detected - inform user to enable faux tagging mode
          openAlert({
            type: 'note',
            title: '',
            message: `This photo has no detected faces. Enable "Use Faux Tagging" mode to tag photos without detected faces.`,
          });
        }
      }

      onResetSelection();
      return;
    }

    // Multi Photo setup.
    requestConfirm({
      actionSize: 'sm',
      message: (
        <div>
          <h4 className="text-center">
            Tag this member to {pluralize('photo', selectedPhotos.length, true)}
          </h4>
          <div className="text-center">
            <IdentityAvatar
              key={identity.id}
              identity={identity}
              selfieType="any"
            />
            <p className="text-center text-sm text-uppercase text-faded text-bold mt-1">
              {getIdentityFullName(identity, identity.account)}
            </p>
          </div>
        </div>
      ),
      onCancel: () => {},
      confirmText: 'Confirm',
      onConfirm: async () => {
        let successCount = 0;
        let failureCount = 0;

        if (useFauxTaggingMode) {
          // Faux tagging mode: Use faux tags for ALL photos
          const successfulPhotoIds = [];

          for (const photoId of selectedPhotos) {
            try {
              const photo = photos.find(({ id }) => id === photoId);
              const result = await onIdentityPubBoxCreate({
                identityId: identity?.id,
                photoId: photo?.id,
              });

              if (result.success) {
                successCount++;
                successfulPhotoIds.push(photoId);
              } else {
                failureCount++;
              }
            } catch (error) {
              failureCount++;
            }
          }

          if (successCount === selectedPhotos.length) {
            setToast(
              `${identity.name} has been tagged on all ${pluralize(
                'photo',
                successCount,
                true,
              )}!`,
            );
            onResetSelection();
          } else if (successCount > 0) {
            // Deselect successfully tagged photos
            onDeselectPhotos(successfulPhotoIds);

            openAlert({
              type: 'note',
              title: '',
              message: `Tagged ${pluralize(
                'photo',
                successCount,
                true,
              )}. ${pluralize(
                'photo',
                failureCount,
                true,
              )} failed to tag and remain selected.`,
            });
          } else {
            openAlert({
              type: 'danger',
              title: 'Error',
              message: 'Failed to tag any photos. Please try again later.',
            });
          }
        } else {
          // Current behavior: Only tag photos with detected faces
          const photosWithFaces = [];

          // Separate photos into those with and without detected faces
          selectedPhotos.forEach(photoId => {
            const photo = photos.find(({ id }) => id === photoId);
            const faces = photo?.faces?.records ?? [];
            const largestFace = getLargestFace(faces);

            if (largestFace && faces.length === 1) {
              photosWithFaces.push({ photo, largestFace });
            }
            // Photos with multiple faces or no faces are skipped
          });

          // Handle photos with detected faces using bulk vote
          if (photosWithFaces.length > 0) {
            const votes = photosWithFaces.map(({ photo, largestFace }) => ({
              identity_id: identity?.id,
              compared_face_id: largestFace?.id,
              compared_photo_id: photo?.id,
              selfie_face_id: currentFace?.id ?? matchingFace?.id,
              decision: VOTE_DECISION.TOTALLY,
              judge_id: account?.id,
              is_consumer: false,
              applied: true,
            }));

            try {
              await onBulkVote({ votes });
              successCount += photosWithFaces.length;
            } catch (error) {
              failureCount += photosWithFaces.length;
            }
          }

          // Calculate photos that were skipped (no faces or multiple faces)
          const skippedCount = selectedPhotos.length - photosWithFaces.length;

          if (successCount === selectedPhotos.length) {
            setToast(
              `${identity.name} has been matched on all ${pluralize(
                'photo',
                successCount,
                true,
              )}!`,
            );
            onResetSelection();
          } else if (successCount > 0) {
            const taggedPhotoIds = photosWithFaces.map(({ photo }) => photo.id);
            onDeselectPhotos(taggedPhotoIds);

            let message = `Matched ${pluralize('photo', successCount, true)}.`;
            if (skippedCount > 0) {
              message += ` ${pluralize(
                'photo',
                skippedCount,
                true,
              )} without detected faces or with multiple faces were not tagged and remain selected. Enable "Use Faux Tagging" to tag photos without detected faces.`;
            }

            openAlert({
              type: 'note',
              title: '',
              message,
            });
          } else {
            openAlert({
              type: 'note',
              title: '',
              message:
                'No photos could be tagged. Enable "Use Faux Tagging" mode to tag photos without detected faces.',
            });
          }
        }
      },
    });
  };

  const onRemoveFaceTags = async () => {
    requestConfirm({
      actionSize: 'sm',
      message: (
        <div>
          <h4 className="text-center">
            Remove all face tags from{' '}
            {pluralize('photo', selectedPhotos.length, true)}?
          </h4>
          <p className="text-center">
            This action cannot be undone. You will need to reapply face tags to{' '}
            {pluralize('this', selectedPhotos.length)}{' '}
            {pluralize('photo', selectedPhotos.length)} manually.
          </p>
        </div>
      ),
      onCancel: () => {},
      confirmText: 'Remove tags',
      confirmBtnStyle: 'danger',
      onConfirm: async () => {
        try {
          // Get all photos with their faces
          const photosWithFaceTags = selectedPhotos
            .map(photoId => photos.find(({ id }) => id === photoId))
            .filter(photo => photo?.faces?.records?.length > 0);

          if (photosWithFaceTags.length === 0) {
            return;
          }

          const votes = [];
          await pMap(
            photosWithFaceTags,
            async photo => {
              const faces = photo?.faces?.records || [];
              const photoVotes = faces
                .map(face => ({
                  identity_id: face.matched_photos?.records?.[0]?.identity?.id,
                  compared_face_id: face.id,
                  compared_photo_id: photo.id,
                  selfie_face_id:
                    face.matched_photos?.records?.[0]?.identity
                      ?.current_matching_face?.id,
                  decision: VOTE_DECISION.NOPE,
                  judge_id: account?.id,
                  is_consumer: false,
                  applied: true,
                }))
                .filter(vote => vote.identity_id && vote.selfie_face_id);

              votes.push(...photoVotes);
            },
            { concurrency: 5 },
          );

          // Submit votes in batches to avoid overwhelming the server
          await onBulkVote({
            votes,
          });

          setToast(
            `Removed face tags from ${pluralize(
              'photo',
              photosWithFaceTags.length,
              true,
            )}.`,
          );
          onResetSelection();
        } catch (error) {
          openAlert({
            type: 'danger',
            title: 'Error',
            message:
              'There was an error removing face tags. Please try again later.',
            info: error.message,
          });
        }
      },
    });
  };

  const onAddFauxFaceTags = () => {
    requestConfirm({
      actionSize: 'md',
      message: (
        <div>
          <h4 className="text-center mb-3">
            Select a member to apply faux face tags to{' '}
            {pluralize('photo', selectedPhotos.length, true)}
          </h4>
          <div className="faux-tagging-identity-list">
            {availableIdentities.map(identity => (
              <div
                key={identity.id}
                className="faux-tagging-identity-item d-flex align-items-center"
                onClick={async () => {
                  // Close the modal and process the tagging
                  closeModal(CONFIRM_MODAL);

                  let successCount = 0;
                  let failureCount = 0;
                  const successfulPhotoIds = [];

                  for (const photoId of selectedPhotos) {
                    try {
                      const photo = photos.find(({ id }) => id === photoId);
                      const result = await onIdentityPubBoxCreate({
                        identityId: identity?.id,
                        photoId: photo?.id,
                      });

                      if (result.success) {
                        successCount++;
                        successfulPhotoIds.push(photoId);
                      } else {
                        failureCount++;
                      }
                    } catch (error) {
                      failureCount++;
                    }
                  }

                  if (successCount === selectedPhotos.length) {
                    setToast(
                      `${identity.name} has been tagged on all ${pluralize(
                        'photo',
                        successCount,
                        true,
                      )}!`,
                    );
                    await refetch();
                    onResetSelection();
                  } else if (successCount > 0) {
                    await refetch();
                    onDeselectPhotos(successfulPhotoIds);

                    openAlert({
                      type: 'note',
                      title: '',
                      message: `Tagged ${pluralize(
                        'photo',
                        successCount,
                        true,
                      )}. ${pluralize(
                        'photo',
                        failureCount,
                        true,
                      )} failed to tag and remain selected.`,
                    });
                  } else {
                    openAlert({
                      type: 'danger',
                      title: 'Error',
                      message:
                        'Failed to tag any photos. Please try again later.',
                    });
                  }
                }}
              >
                <IdentityAvatar
                  identity={identity}
                  selfieType="any"
                  className="mr-3"
                />
                <div>
                  <p className="mb-0 text-bold">
                    {getIdentityFullName(identity, identity.account)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      confirmText: 'Cancel',
      confirmBtnStyle: 'outline-primary',
      onConfirm: () => {
        closeModal(CONFIRM_MODAL);
      },
    });
  };

  const {
    app: { adminUrl },
  } = getConfig();

  return (
    <Fragment>
      <DndContext
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        sensors={[mouseSensor, touchSensor]}
      >
        {headerless && (
          <div className="d-flex w-100 justify-content-between p-2">
            <div>
              Album:{' '}
              <a
                href={`${adminUrl}/albums/${album?.id}/photos/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-extra-bold"
              >
                {album?.name} ({album?.root_album_folder?.name})
              </a>
            </div>
            <div>
              {photosTotal > 0 &&
                (selectedPhotos ?? [])?.length === 0 &&
                !selectionEnabled && (
                  <Button
                    btnStyle="link"
                    className="text-extra-bold"
                    btnSize="sm"
                    onClick={() => onToggleSelectionEnabled()}
                  >
                    Select Photos
                  </Button>
                )}
              {((selectedPhotos ?? [])?.length > 0 || selectionEnabled) && (
                <Button
                  btnStyle="link"
                  className="text-extra-bold text-danger"
                  btnSize="sm"
                  onClick={() => onResetSelection()}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
        {!headerless && (
          <AlbumManualMatchingHeader
            album={album}
            totalPhotos={photosTotal}
            useFauxTaggingMode={useFauxTaggingMode}
            setUseFauxTaggingMode={setUseFauxTaggingMode}
          />
        )}
        {selectionEnabled && (
          <AlbumManualMatchingToolbar
            dismissPhotoGroupToolbar={onResetSelection}
            selectedPhotos={selectedPhotos}
            openModal={openModal}
            totalPhotos={photosTotal}
            disabled={loading}
            movePhotosDisabled={false}
            onPhotosDownload={onPhotosDownload}
            onPhotosHide={() =>
              onPhotosHide({ selectedPhotos, onResetSelection })
            }
            onPhotosDelete={() =>
              onPhotosDelete({ selectedPhotos, onResetSelection })
            }
            onFaceTagModeToggle={() => setFaceTagModePhotoId(selectedPhotos[0])}
            onRemoveFaceTags={onRemoveFaceTags}
            onAddFauxFaceTags={onAddFauxFaceTags}
            availableIdentities={availableIdentities}
            album={album}
          />
        )}
        <div
          className={classNames({
            'manual-matching-container': true,
            headerless,
          })}
        >
          <IdentitySelector
            album={album}
            onIdentitiesLoaded={setAvailableIdentities}
          />
          <div ref={scrollRef}>
            <div className="d-flex align-items-center mb-2">
              <div className="dropdown-menu-dark ml-0">
                <DropdownMenu
                  closeOnClickWithin
                  toggle={
                    <div className="text-bold">
                      {filter.photosFilter} <Angle direction="down" />
                    </div>
                  }
                >
                  {Object.values(ALBUM_MANUAL_MATCHING_FILTERS).map(value => (
                    <Button
                      key={value}
                      btnSize="sm"
                      btnStyle="reset"
                      className="dropdown-item"
                      disabled={selectionEnabled}
                      onClick={() =>
                        onFilterUpdate({ photosFilter: value, offset: 0 })
                      }
                    >
                      {value}
                    </Button>
                  ))}
                </DropdownMenu>
              </div>
            </div>
            {!loading && !photos?.length && (
              <Alert type="info" className="my-2 z-neg">
                No photos found for this filter selection
              </Alert>
            )}
            <ShiftSelect
              items={photos?.map(photo => photo.id)}
              onSelectItems={onSelectPhotos}
              onDeselectItems={onDeselectPhotos}
            >
              {({ onToggleSelectedItem: onToggleSelectedPhoto }) => (
                <Fragment>
                  <PhotoGrid
                    loading={loading}
                    records={photos}
                    total={photosTotal}
                    loadMore={loadMore}
                    getShouldLoadMore={() =>
                      !loading && !loadingMore && photosTotal > photos.length
                    }
                    loadingComponent={
                      <div className="text-center">
                        <Spinner />
                      </div>
                    }
                    windowScrollerProps={{
                      scrollElement: scrollRef?.current,
                    }}
                    nativePhotoViewer
                    cellRenderer={({ record: photo, recordPath }) => {
                      const cardClasses = classNames({
                        'photo-card-transparent':
                          photo.album_photo_status ===
                          ALBUM_PHOTO_STATUS.UNPUBLISHED,
                      });

                      return (
                        <PhotoGridCardDroppable
                          photo={photo}
                          linkTo={recordPath}
                          className={cardClasses}
                        >
                          {showFileNames && photo.original_filename && (
                            <span className="file-name text-xs">
                              {photo.original_filename}
                            </span>
                          )}
                          <AlbumPhotoMenu
                            photo={photo}
                            album={album}
                            shareActionEnabled={false}
                            coverPhotoActionEnabled={false}
                            onVisibilityToggle={async () => {
                              if (
                                photo.album_photo_status ===
                                ALBUM_PHOTO_STATUS.PUBLISHED
                              ) {
                                await onPhotosHide({
                                  selectedPhotos: [photo.id],
                                  onResetSelection,
                                });
                              } else {
                                await onPhotosPublish({
                                  selectedPhotos: [photo.id],
                                  onResetSelection,
                                });
                              }
                            }}
                            onSelectToggle={() =>
                              onToggleSelectedPhoto(
                                photo.id,
                                selectedPhotos.includes(photo.id),
                              )
                            }
                            isSelected={selectedPhotos.includes(photo.id)}
                            selectable={selectionEnabled}
                            isHidden={
                              photo.album_photo_status !==
                              ALBUM_PHOTO_STATUS.PUBLISHED
                            }
                            onViewPhotoUniverse={
                              window
                                ? () => {
                                    window.open(
                                      `/app/${basename}/albums/${albumId}/photos?tab=ALL&targetPhotoId=${photo.id}`,
                                      '_blank',
                                    );
                                  }
                                : undefined
                            }
                            onDelete={() => {
                              onPhotosDelete({
                                selectedPhotos: [photo.id],
                                onResetSelection,
                              });
                            }}
                            hasSlideshow={false}
                          />
                        </PhotoGridCardDroppable>
                      );
                    }}
                    photoViewerProps={{
                      onDismiss: async () => {
                        // Only refetch if we are on unmatched view.
                        if (
                          faceTagTotallyVoteCast &&
                          filter.photosFilter ===
                            ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED
                        ) {
                          await refetch();
                        }
                        setFaceTagTotallyVoteCast(false);
                      },
                      actions: ({
                        record: photo,
                        onPrevRecord,
                        onNextRecord,
                        prevRecordId: photoViewerPrevRecordId,
                        nextRecordId: photoViewerNextRecordId,
                        onNavigateNextAfterRemoval,
                        zoomIn,
                        zoomOut,
                      }) => (
                        <PhotoGroupPhotoViewerActions
                          album={album}
                          photo={photo}
                          onFaceTagTotallyVoteCast={() =>
                            setFaceTagTotallyVoteCast(true)
                          }
                          onDelete={async () => {
                            await onPhotosDelete({
                              selectedPhotos: [photo.id],
                              onResetSelection,
                              onNavigateNextAfterRemoval,
                            });
                          }}
                          onVisibilityToggle={async () => {
                            if (
                              photo.album_photo_status ===
                              ALBUM_PHOTO_STATUS.PUBLISHED
                            ) {
                              await onPhotosHide({
                                selectedPhotos: [photo.id],
                                onResetSelection,
                              });
                            } else {
                              await onPhotosPublish({
                                selectedPhotos: [photo.id],
                                onResetSelection,
                              });
                            }
                          }}
                          onPrevRecord={onPrevRecord}
                          onNextRecord={onNextRecord}
                          prevRecordId={photoViewerPrevRecordId}
                          nextRecordId={photoViewerNextRecordId}
                          zoomIn={zoomIn}
                          zoomOut={zoomOut}
                        />
                      ),
                    }}
                  />
                </Fragment>
              )}
            </ShiftSelect>
          </div>
        </div>
        <DragOverlay>
          {!!draggingIdentity && (
            <IdentitySelectorOption
              isOverlay
              identity={draggingIdentity}
              className="identity-selector-option-draggable-overlay"
            />
          )}
        </DragOverlay>
      </DndContext>
      <PhotoDownloadModal
        totalPhotos={selectedPhotos.length}
        photoDownloadCreateInput={{
          photo_ids: {
            select_all: false,
            selected_ids: selectedPhotos,
            ignored_ids: [],
          },
          album: {
            album_id: album?.id,
            album_photo_group_ids: [],
            ungrouped_only: true,
          },
          album_photo_filter: ALBUM_PHOTO_FILTERS.ALL,
          uploader_ids: [],
        }}
      />
      <PhotoAlbumFolderTagsModal
        selectedPhotos={selectedPhotos}
        albumPhotoFilter={ALBUM_PHOTO_FILTERS.ALL}
      />
      {album?.root_album_folder?.id && (
        <ProMultiTargetSelectionModal
          rootAlbumFolderId={album.root_album_folder.id}
          photoGroupSelectionEnabled
          onConfirm={targets => {
            const photoGroupIds = targets
              .filter(
                target =>
                  !!target.photoGroup?.id &&
                  // covers unlisted case
                  target.id !== target.photoGroup?.id,
              )
              .map(target => target.photoGroup.id);

            const albumIds = targets
              .filter(
                target =>
                  !target.photoGroup?.id ||
                  // covers unlisted case
                  target.id === target.photoGroup?.id,
              )
              .map(({ id }) => id);

            onPhotosMove({
              selectedPhotos,
              onResetSelection,
              albumIds,
              photoGroupIds,
            });
          }}
          disabledTargets={[albumId]}
        />
      )}
      <FaceTagPhotoModal
        forceOpen={faceTagModePhotoId}
        albumId={albumId}
        photoId={faceTagModePhotoId}
        nextRecordId={nextRecordId}
        prevRecordId={prevRecordId}
        onNextRecord={() =>
          setFaceTagModePhotoId(getAdjacentFaceTagModeRecordId(1))
        }
        onPrevRecord={() =>
          setFaceTagModePhotoId(getAdjacentFaceTagModeRecordId(-1))
        }
        onClose={() => {
          setFaceTagModePhotoId(null);
          onResetSelection();
        }}
      />
    </Fragment>
  );
};

AlbumManualMatching.propTypes = propTypes;

export default AlbumManualMatching;
